<template>
  <b-modal ref="filterWindow" title="Flight Filter Window" :visible="value" @change="$emit('input', $event)" size="lg" scrollable>
    <div class="flight-filter-window">
      <div class="row mb-3">
        <div class="col">
          <b-button-group class="float-right" size="sm">
            <b-button @click="openAllFilters()" variant="outline-primary">Expand all</b-button>
            <b-button @click="hideAllFilters()" variant="outline-danger">Collapse all</b-button>
          </b-button-group>
        </div>
      </div>

      <b-form id="flight-filter-form" @submit.stop.prevent="runSearch()">
        <section class="filter-departure filter-section">
          <div class="row">
            <div class="col">
              <button type="button" class="btn-collapse-trigger btn btn-light" @click="toggleCollapse('isDepartureCollapseOpen')">
                <div class="text-center text-lg-left font-weight-bold">Departure</div>
                <div class="collapse-icon text-right" :class="{ active: uiControls.isDepartureCollapseOpen }"></div>
              </button>
            </div>
          </div>

          <b-collapse id="departure-collapse" class="filter-collapse" :visible="uiControls.isDepartureCollapseOpen">
            <div class="filter-collapse-content">
              <div class="row align-items-start">
                <div class="col-12"><span class="filter-date-label">Time of departure</span></div>
                <template v-if="!formControls.dptDt.range">
                  <b-form-group class="col-12 col-md" label="Date" label-for="input-depart-date" label-class="filter-date-sub-label">
                    <b-form-datepicker id="input-depart-date" v-model="form.dptDt.eq" size="sm" reset-button></b-form-datepicker>
                  </b-form-group>
                </template>
                <template v-else>
                  <b-form-group class="col-12 col-md" label="Start date" label-for="input-depart-start-date" label-class="filter-date-sub-label">
                    <b-form-datepicker id="input-depart-start-date" v-model="form.dptDt.gte" :max="departMaxDate" size="sm" reset-button></b-form-datepicker>
                  </b-form-group>
                  <b-form-group class="col-12 col-md" label="End date" label-for="input-depart-end-date" label-class="filter-date-sub-label">
                    <b-form-datepicker id="input-depart-end-date" v-model="form.dptDt.lte" :min="departMinDate" size="sm" reset-button></b-form-datepicker>
                  </b-form-group>
                </template>

                <b-form-group class="col-12 col-md-2" label="Toggle range" label-for="input-toggle-range-depart-date" label-class="filter-date-sub-label">
                  <b-form-checkbox
                    id="input-toggle-range-depart-date"
                    class="d-flex justify-content-lg-center"
                    v-model="formControls.dptDt.range"
                    @input="toggleRange('dptDt')"
                    switch
                  ></b-form-checkbox>
                </b-form-group>
              </div>

              <div class="row align-items-start">
                <template v-if="!formControls.dptT.range">
                  <b-form-group class="col-12 col-md" label="Time" label-for="input-depart-time" label-class="filter-date-sub-label">
                    <b-form-timepicker id="input-depart-time" v-model="form.dptT.eq" size="sm" reset-button></b-form-timepicker>
                  </b-form-group>
                </template>
                <template v-else>
                  <b-form-group class="col-12 col-md" label="Start time" label-for="input-depart-start-time" label-class="filter-date-sub-label">
                    <b-form-timepicker id="input-depart-start-time" v-model="form.dptT.gte" size="sm" reset-button></b-form-timepicker>
                  </b-form-group>
                  <b-form-group class="col-12 col-md" label="End time" label-for="input-depart-end-time" label-class="filter-date-sub-label">
                    <b-form-timepicker id="input-depart-end-time" v-model="form.dptT.lte" size="sm" reset-button></b-form-timepicker>
                  </b-form-group>
                </template>

                <b-form-group class="col-12 col-md-2" label="Toggle range" label-for="input-toggle-range-depart-time" label-class="filter-date-sub-label">
                  <b-form-checkbox
                    id="input-toggle-range-depart-time"
                    class="d-flex justify-content-lg-center"
                    v-model="formControls.dptT.range"
                    @input="toggleRange('dptT')"
                    switch
                  ></b-form-checkbox>
                </b-form-group>
              </div>

              <div class="row align-items-center">
                <b-form-group class="col-12 col-md" label="Country" label-for="input-depart-country" label-size="sm">
                  <b-form-select id="input-depart-country" placeholder="Select a country" v-model="form.dptCtry" size="sm">
                    <b-form-select-option :value="null">None selected</b-form-select-option>
                    <b-form-select-option v-for="country of countries" :key="country.CODE2" :value="country.CODE2">
                      {{ `${country.COUNTRYNAME} (${country.CODE3})` }}
                    </b-form-select-option>
                  </b-form-select>
                </b-form-group>
                <b-form-group class="col-12 col-md" label="Port name" label-for="input-depart-port" label-size="sm">
                  <b-form-input id="input-depart-port" type="text" placeholder="e.g. London City Airport" v-model="form.dptPrt" size="sm"></b-form-input>
                </b-form-group>
              </div>
            </div>
          </b-collapse>
        </section>

        <section class="filter-arrival filter-section">
          <div class="row">
            <div class="col">
              <button type="button" class="btn-collapse-trigger btn btn-light" @click="toggleCollapse('isArrivalCollapseOpen')">
                <div class="text-center text-lg-left font-weight-bold">Arrival</div>
                <div class="collapse-icon text-right" :class="{ active: uiControls.isArrivalCollapseOpen }"></div>
              </button>
            </div>
          </div>

          <b-collapse id="arrival-collapse" class="filter-collapse" :visible="uiControls.isArrivalCollapseOpen">
            <div class="filter-collapse-content">
              <div class="row align-items-start">
                <div class="col-12"><span class="filter-date-label">Time of arrival</span></div>
                <template v-if="!formControls.arrDt.range">
                  <b-form-group class="col-12 col-md" label="Date" label-for="input-arrive-date" label-class="filter-date-sub-label">
                    <b-form-datepicker id="input-arrive-date" v-model="form.arrDt.eq" size="sm" reset-button></b-form-datepicker>
                  </b-form-group>
                </template>
                <template v-else>
                  <b-form-group class="col-12 col-md" label="Start date" label-for="input-arrive-start-date" label-class="filter-date-sub-label">
                    <b-form-datepicker id="input-arrive-start-date" v-model="form.arrDt.gte" :max="arriveMaxDate" size="sm" reset-button></b-form-datepicker>
                  </b-form-group>
                  <b-form-group class="col-12 col-md" label="End date" label-for="input-arrive-end-date" label-class="filter-date-sub-label">
                    <b-form-datepicker id="input-arrive-end-date" v-model="form.arrDt.lte" :min="arriveMinDate" size="sm" reset-button></b-form-datepicker>
                  </b-form-group>
                </template>

                <b-form-group class="col-12 col-md-2" label="Toggle range" label-for="input-toggle-range-arrive-date" label-class="filter-date-sub-label">
                  <b-form-checkbox
                    id="input-toggle-range-arrive-date"
                    class="d-flex justify-content-lg-center"
                    v-model="formControls.arrDt.range"
                    @input="toggleRange('arrDt')"
                    switch
                  ></b-form-checkbox>
                </b-form-group>
              </div>

              <div class="row align-items-start">
                <template v-if="!formControls.arrT.range">
                  <b-form-group class="col-12 col-md" label="Time" label-for="input-arrive-time" label-class="filter-date-sub-label">
                    <b-form-timepicker id="input-arrive-time" v-model="form.arrT.eq" size="sm" reset-button></b-form-timepicker>
                  </b-form-group>
                </template>
                <template v-else>
                  <b-form-group class="col-12 col-md" label="Start time" label-for="input-arrive-start-time" label-class="filter-date-sub-label">
                    <b-form-timepicker id="input-arrive-start-time" v-model="form.arrT.gte" size="sm" reset-button></b-form-timepicker>
                  </b-form-group>
                  <b-form-group class="col-12 col-md" label="End time" label-for="input-arrive-end-time" label-class="filter-date-sub-label">
                    <b-form-timepicker id="input-arrive-end-time" v-model="form.arrT.lte" size="sm" reset-button></b-form-timepicker>
                  </b-form-group>
                </template>

                <b-form-group class="col-12 col-md-2" label="Toggle range" label-for="input-toggle-range-arrive-time" label-class="filter-date-sub-label">
                  <b-form-checkbox
                    id="input-toggle-range-arrive-time"
                    class="d-flex justify-content-lg-center"
                    v-model="formControls.arrT.range"
                    @input="toggleRange('arrT')"
                    switch
                  ></b-form-checkbox>
                </b-form-group>
              </div>

              <div class="row align-items-center">
                <b-form-group class="col-12 col-md" label="Country" label-for="input-arrive-country" label-size="sm">
                  <b-form-select id="input-arrive-country" placeholder="Select a country" v-model="form.arrCtry" size="sm">
                    <b-form-select-option :value="null">None selected</b-form-select-option>
                    <b-form-select-option v-for="country of countries" :key="country.CODE2" :value="country.CODE2">
                      {{ `${country.COUNTRYNAME} (${country.CODE3})` }}
                    </b-form-select-option>
                  </b-form-select>
                </b-form-group>
                <b-form-group class="col-12 col-md" label="Port name" label-for="input-arrive-port" label-size="sm">
                  <b-form-input id="input-arrive-port" type="text" placeholder="e.g. Piarco Airport" v-model="form.arrPrt" size="sm"></b-form-input>
                </b-form-group>
              </div>
            </div>
          </b-collapse>
        </section>

        <section class="filter-carrier filter-section">
          <div class="row">
            <div class="col">
              <button type="button" class="btn-collapse-trigger btn btn-light" @click="toggleCollapse('isCarrierCollapseOpen')">
                <div class="text-center text-lg-left font-weight-bold">Aircraft Carrier</div>
                <div class="collapse-icon text-right" :class="{ active: uiControls.isCarrierCollapseOpen }"></div>
              </button>
            </div>
          </div>

          <b-collapse id="carrier-collapse" class="filter-collapse" :visible="uiControls.isCarrierCollapseOpen">
            <div class="filter-collapse-content">
              <div class="row align-items-center">
                <b-form-group class="col-12 col-md" label="Carrier ID" label-for="input-carrier-id" label-size="sm">
                  <b-form-input id="input-carrier-id" type="text" placeholder="e.g. AC966" v-model="form.cId" size="sm"></b-form-input>
                </b-form-group>
                <b-form-group class="col-12 col-md" label="Carrier name" label-for="input-carrier-name" label-size="sm">
                  <b-form-input id="input-carrier-name" type="text" placeholder="e.g. Air Jordan" v-model="form.cNm" size="sm"></b-form-input>
                </b-form-group>
              </div>
            </div>
          </b-collapse>
        </section>

        <section class="filter-traveler filter-section">
          <div class="row">
            <div class="col">
              <button type="button" class="btn-collapse-trigger btn btn-light" @click="toggleCollapse('isTravelerCollapseOpen')">
                <div class="text-center text-lg-left font-weight-bold">Traveler</div>
                <div class="collapse-icon text-right" :class="{ active: uiControls.isTravelerCollapseOpen }"></div>
              </button>
            </div>
          </div>

          <b-collapse id="traveler-collapse" class="filter-collapse" :visible="uiControls.isTravelerCollapseOpen">
            <div class="filter-collapse-content">
              <div class="row align-items-center">
                <b-form-group class="col-12 col-md" label="First name" label-for="input-traveler-first-name" label-size="sm">
                  <b-form-input id="input-traveler-first-name" type="text" placeholder="e.g. John" v-model="form.t.fNm" size="sm"></b-form-input>
                </b-form-group>
                <b-form-group class="col-12 col-md" label="Last name" label-for="input-traveler-last-name" label-size="sm">
                  <b-form-input id="input-traveler-last-name" type="text" placeholder="e.g. Hacket" v-model="form.t.lNm" size="sm"></b-form-input>
                </b-form-group>
              </div>
            </div>
          </b-collapse>
        </section>
      </b-form>
    </div>

    <template #modal-footer>
      <button type="reset" class="btn btn-outline-secondary float-right" form="flight-filter-form" @click="resetFilters()">Reset</button>
      <button type="submit" class="btn btn-primary ml-2 float-right" form="flight-filter-form">Search</button>
    </template>
  </b-modal>
</template>

<script>
import FilterMixin from '../../mixins/filter.mixin';
import _ from '../../utils/lodash.util';

export default {
  name: 'FilterFlights',

  mixins: [FilterMixin],

  data() {
    // Get query params from route
    const routeQueries = this.$router.currentRoute.query;

    // Populate form with query params
    let form = {
      // Departure data
      dptDt: routeQueries['dptDt'] ? { gte: routeQueries['dptDt'].gte, lte: routeQueries['dptDt'].lte, eq: routeQueries['dptDt'].eq } : { gte: null, lte: null, eq: null },
      dptT: routeQueries['dptT'] ? { gte: routeQueries['dptT'].gte, lte: routeQueries['dptT'].lte, eq: routeQueries['dptT'].eq } : { gte: null, lte: null, eq: null },
      dptCtry: routeQueries['dptCtry'] || null,
      dptPrt: routeQueries['dptPrt'] || null,

      // Arrival data
      arrDt: routeQueries['arrDt'] ? { gte: routeQueries['arrDt'].gte, lte: routeQueries['arrDt'].lte, eq: routeQueries['arrDt'].eq } : { gte: null, lte: null, eq: null },
      arrT: routeQueries['arrT'] ? { gte: routeQueries['arrT'].gte, lte: routeQueries['arrT'].lte, eq: routeQueries['arrT'].eq } : { gte: null, lte: null, eq: null },
      arrCtry: routeQueries['arrCtry'] || null,
      arrPrt: routeQueries['arrPrt'] || null,

      // Aircraft carrier data
      cId: routeQueries['cId'] || null,
      cNm: routeQueries['cNm'] || null,

      // Traveler data
      t: routeQueries['t'] ? { fNm: routeQueries['t'].fNm, lNm: routeQueries['t'].lNm } : { fNm: null, lNm: null }
    };

    // Form controls
    let formControls = {
      // Set inital range controls based on if range fields (gte or lte) set.
      dptDt: { range: Boolean(form.dptDt.gte || form.dptDt.lte) },
      dptT: { range: Boolean(form.dptT.gte || form.dptT.lte) },
      arrDt: { range: Boolean(form.arrDt.gte || form.arrDt.lte) },
      arrT: { range: Boolean(form.arrT.gte || form.arrT.lte) }
    };

    // Check if initial form values for each section are empty
    const departureFormFields = _.removeEmptyValues(_.pick(form, ['dptDt', 'dptT', 'dptCtry', 'dptPrt']));
    const arrivalFormFields = _.removeEmptyValues(_.pick(form, ['arrDt', 'arrT', 'arrCtry', 'arrPrt']));
    const carrierFormFields = _.removeEmptyValues(_.pick(form, ['cId', 'cNm']));
    const travelerFormFields = _.removeEmptyValues(_.pick(form, ['t']));

    // UI controls
    let uiControls = {
      // If one or more fields in section are not empty, open collapse initially
      isDepartureCollapseOpen: !_.isEmpty(departureFormFields),
      isArrivalCollapseOpen: !_.isEmpty(arrivalFormFields),
      isCarrierCollapseOpen: !_.isEmpty(carrierFormFields),
      isTravelerCollapseOpen: !_.isEmpty(travelerFormFields)
    };

    // Send initial filters to parent
    this.$emit('filtersSetup', _.removeEmptyValues(form));

    return { form, formControls, uiControls };
  },

  computed: {
    departMinDate() {
      return this.$moment(this.form.dptDt.gte).format('YYYY-MM-DD');
    },

    departMaxDate() {
      return this.$moment(this.form.dptDt.lte).format('YYYY-MM-DD');
    },

    arriveMinDate() {
      return this.$moment(this.form.arrDt.gte).format('YYYY-MM-DD');
    },

    arriveMaxDate() {
      return this.$moment(this.form.arrDt.lte).format('YYYY-MM-DD');
    }
  },

  methods: {
    toggleRange(filterName) {
      if (this.formControls[filterName].range === true) {
        this.form[filterName].eq = null;
      } else {
        this.form[filterName].gte = null;
        this.form[filterName].lte = null;
      }
    },

    resetFilters() {
      this.form = {
        dptDt: { gte: null, lte: null, eq: null },
        dptT: { gte: null, lte: null, eq: null },
        dptCtry: null,
        dptPrt: null,
        arrDt: { gte: null, lte: null, eq: null },
        arrT: { gte: null, lte: null, eq: null },
        arrCtry: null,
        arrPrt: null,
        cId: null,
        cNm: null,
        t: { fNm: null, lNm: null }
      };

      this.formControls = {
        dptDt: { range: false },
        dptT: { range: false },
        arrDt: { range: false },
        arrT: { range: false }
      };
    }
  }
};
</script>

<style lang="scss" src="../../assets/styles/filter.scss" scoped></style>
